import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  LnpCreateRequest,
  LnpUpdateRequest,
  LnpListQuery,
  LnpListResponse,
  LnpResponse,
} from '@produzionejb/types';
import { api } from './api-client';

const QK = {
  list: (q: Partial<LnpListQuery>) => ['lnp-documents', 'list', q] as const,
  one: (id: string) => ['lnp-documents', 'one', id] as const,
};

function toQueryString(q: Partial<LnpListQuery>): string {
  const params = new URLSearchParams();
  if (q.page) params.set('page', String(q.page));
  if (q.pageSize) params.set('pageSize', String(q.pageSize));
  if (q.status) params.set('status', q.status);
  if (q.periodId) params.set('periodId', q.periodId);
  if (q.search) params.set('search', q.search);
  if (q.workDateFrom) params.set('workDateFrom', new Date(q.workDateFrom).toISOString());
  if (q.workDateTo) params.set('workDateTo', new Date(q.workDateTo).toISOString());
  const s = params.toString();
  return s ? `?${s}` : '';
}

export function useLnpList(query: Partial<LnpListQuery>) {
  return useQuery({
    queryKey: QK.list(query),
    queryFn: () => api<LnpListResponse>(`/api/documents/lnp${toQueryString(query)}`),
    staleTime: 10_000,
  });
}

export function useLnp(id: string | undefined) {
  return useQuery({
    queryKey: id ? QK.one(id) : ['lnp-documents', 'one', 'NIL'],
    queryFn: () => api<LnpResponse>(`/api/documents/lnp/${id}`),
    enabled: !!id,
  });
}

export function useCreateLnp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: LnpCreateRequest) =>
      api<LnpResponse>('/api/documents/lnp', { method: 'POST', body: data }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['lnp-documents', 'list'] });
    },
  });
}

export function useUpdateLnp(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: LnpUpdateRequest) =>
      api<LnpResponse>(`/api/documents/lnp/${id}`, { method: 'PATCH', body: data }),
    onSuccess: (updated) => {
      qc.setQueryData(QK.one(id), updated);
      void qc.invalidateQueries({ queryKey: ['lnp-documents', 'list'] });
    },
  });
}

export function useCloseLnp(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<LnpResponse>(`/api/documents/lnp/${id}/close`, { method: 'POST', body: {} }),
    onSuccess: (updated) => {
      qc.setQueryData(QK.one(id), updated);
      void qc.invalidateQueries({ queryKey: ['lnp-documents', 'list'] });
    },
  });
}

export function useDeleteLnp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/api/documents/lnp/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['lnp-documents', 'list'] });
    },
  });
}
